/**
 * ProviderFormFields — shared presentational form fields for provider create/edit flows.
 *
 * Deliberately kept stateless (all state lives in the parent page component) so
 * the same markup can be reused for the Add New Provider flow (Feature C) and any
 * future edit flows on the Provider Profile page (Feature E).
 */

import { ChevronDown, Check, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import * as Select from "@radix-ui/react-select";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cn } from "@/lib/utils";
import { SchemaStatusBadge } from "@/components/schema/SchemaStatusBadge";
import { PROVIDER_TYPES, PROVIDER_TYPE_LABELS } from "@/types/providers";
import type { ProviderType } from "@/types/providers";
import type { TargetSchema } from "@/types";

// ── Uniqueness field validation states ───────────────────────────────────────

export type UniquenessState = "idle" | "checking" | "available" | "taken";

// ── Props ─────────────────────────────────────────────────────────────────────

export interface ProviderFormFieldsProps {
  // Provider Name
  name: string;
  onNameChange: (value: string) => void;
  onNameBlur: () => void;
  nameUniqueness: UniquenessState;
  nameError: string | null;

  // Short Code
  shortCode: string;
  onShortCodeChange: (value: string) => void;
  onShortCodeBlur: () => void;
  shortCodeUniqueness: UniquenessState;
  shortCodeError: string | null;

  // Provider Type
  providerType: ProviderType | "";
  onProviderTypeChange: (value: ProviderType) => void;

  // Schema Assignments
  selectedSchemaIds: string[];
  onSchemaToggle: (schemaId: string) => void;
  schemas: TargetSchema[];
  schemasLoading: boolean;

  /** When false, hides the schema multi-select (identity-only edit). Default true. */
  showSchemas?: boolean;

  disabled?: boolean;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface FieldLabelProps {
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}

/** Consistent label styling used across all form fields. */
function FieldLabel({ htmlFor, children, required = false }: FieldLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-sm font-semibold text-[var(--color-text-primary)]"
    >
      {children}
      {required && <span className="ml-1 text-[var(--color-error)]">*</span>}
    </label>
  );
}

interface UniquenessIndicatorProps {
  state: UniquenessState;
  availableText: string;
  takenText: string;
}

/** Inline icon + text shown to the right of a field while checking uniqueness. */
function UniquenessIndicator({ state, availableText, takenText }: UniquenessIndicatorProps) {
  if (state === "idle") return null;
  if (state === "checking") {
    return (
      <span className="flex items-center gap-1 text-xs text-[var(--color-text-tertiary)]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking…
      </span>
    );
  }
  if (state === "available") {
    return (
      <span className="flex items-center gap-1 text-xs text-[var(--color-success)]">
        <CheckCircle className="h-3.5 w-3.5" />
        {availableText}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-[var(--color-error)]">
      <AlertCircle className="h-3.5 w-3.5" />
      {takenText}
    </span>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * Renders the four provider form fields (Name, Short Code, Type, Schemas).
 * All event handlers and validation state are injected from the parent — this
 * component contains no side effects or local state.
 */
export function ProviderFormFields({
  name,
  onNameChange,
  onNameBlur,
  nameUniqueness,
  nameError,
  shortCode,
  onShortCodeChange,
  onShortCodeBlur,
  shortCodeUniqueness,
  shortCodeError,
  providerType,
  onProviderTypeChange,
  selectedSchemaIds,
  onSchemaToggle,
  schemas,
  schemasLoading,
  showSchemas = true,
  disabled = false,
}: ProviderFormFieldsProps) {
  const inputClass = (hasError: boolean) =>
    cn(
      "h-10 w-full rounded-md border px-3 text-sm outline-none transition",
      hasError
        ? "border-[var(--color-error)] focus:border-[var(--color-error)]"
        : "border-[var(--color-border)] focus:border-[var(--color-action-primary)]",
      disabled && "opacity-50 cursor-not-allowed bg-[var(--color-bg-secondary)]"
    );

  const selectedSchemaNames = schemas
    .filter((s) => selectedSchemaIds.includes(s.id))
    .map((s) => s.name);

  return (
    <div className="flex flex-col gap-6">
      {/* ── Provider Name ── */}
      <div>
        <FieldLabel htmlFor="provider-name" required>
          Provider Name
        </FieldLabel>
        <input
          id="provider-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={onNameBlur}
          placeholder="e.g. Acme Insurance"
          disabled={disabled}
          className={inputClass(!!nameError || nameUniqueness === "taken")}
          aria-describedby={nameError ? "provider-name-error" : undefined}
        />
        <div className="mt-1.5 flex items-center justify-between min-h-[18px]">
          {nameError ? (
            <p id="provider-name-error" className="text-xs text-[var(--color-error)]">
              {nameError}
            </p>
          ) : (
            <span />
          )}
          <UniquenessIndicator
            state={nameUniqueness}
            availableText="Name available"
            takenText="Name already taken"
          />
        </div>
      </div>

      {/* ── Short Code ── */}
      <div>
        <FieldLabel htmlFor="provider-short-code" required>
          Short Code
          <span className="ml-1.5 text-xs font-normal text-[var(--color-text-tertiary)]">
            1–10 uppercase letters, numbers or hyphens — auto-suggested from name
          </span>
        </FieldLabel>
        <input
          id="provider-short-code"
          type="text"
          value={shortCode}
          onChange={(e) => onShortCodeChange(e.target.value.toUpperCase())}
          onBlur={onShortCodeBlur}
          placeholder="e.g. ACME1"
          maxLength={10}
          disabled={disabled}
          className={inputClass(!!shortCodeError || shortCodeUniqueness === "taken")}
          aria-describedby={shortCodeError ? "provider-short-code-error" : undefined}
        />
        <div className="mt-1.5 flex items-center justify-between min-h-[18px]">
          {shortCodeError ? (
            <p id="provider-short-code-error" className="text-xs text-[var(--color-error)]">
              {shortCodeError}
            </p>
          ) : (
            <span />
          )}
          <UniquenessIndicator
            state={shortCodeUniqueness}
            availableText="Code available"
            takenText="Code already taken"
          />
        </div>
      </div>

      {/* ── Provider Type ── */}
      <div>
        <FieldLabel required>Provider Type</FieldLabel>
        <Select.Root
          value={providerType}
          onValueChange={(v) => onProviderTypeChange(v as ProviderType)}
          disabled={disabled}
        >
          <Select.Trigger
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-md border px-3 text-sm outline-none transition",
              "border-[var(--color-border)] hover:border-[var(--color-action-primary)]",
              "focus:border-[var(--color-action-primary)]",
              "data-[placeholder]:text-[var(--color-text-tertiary)]",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <Select.Value placeholder="Select a provider type…" />
            <Select.Icon>
              <ChevronDown className="h-4 w-4 text-[var(--color-text-tertiary)]" />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              className="z-50 overflow-hidden rounded-lg border border-[var(--color-border)] bg-white shadow-lg animate-in fade-in-0 zoom-in-95"
              position="popper"
              sideOffset={4}
            >
              <Select.Viewport className="p-1">
                {PROVIDER_TYPES.map((type) => (
                  <Select.Item
                    key={type}
                    value={type}
                    className="relative flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2.5 text-sm outline-none data-[highlighted]:bg-[var(--color-bg-secondary)]"
                  >
                    <Select.ItemText>{PROVIDER_TYPE_LABELS[type]}</Select.ItemText>
                    <Select.ItemIndicator className="ml-auto">
                      <Check className="h-4 w-4 text-[var(--color-brand-primary)]" />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {/* ── Schema Assignments ── */}
      {showSchemas && (
        <div>
          <FieldLabel>Schemas</FieldLabel>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                disabled={disabled || schemasLoading}
                className={cn(
                  "flex h-10 w-full items-center justify-between rounded-md border px-3 text-sm outline-none transition",
                  "border-[var(--color-border)] hover:border-[var(--color-action-primary)]",
                  "focus:border-[var(--color-action-primary)]",
                  (disabled || schemasLoading) && "opacity-50 cursor-not-allowed"
                )}
              >
                {schemasLoading ? (
                  <span className="flex items-center gap-1.5 text-[var(--color-text-tertiary)]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading schemas…
                  </span>
                ) : selectedSchemaNames.length === 0 ? (
                  <span className="text-[var(--color-text-tertiary)]">Select schemas…</span>
                ) : (
                  <span className="truncate text-[var(--color-text-primary)]">
                    {selectedSchemaNames.join(", ")}
                  </span>
                )}
                <ChevronDown className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-60 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-white p-1 shadow-lg animate-in fade-in-0 zoom-in-95"
                sideOffset={4}
                align="start"
              >
                {schemas.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-[var(--color-text-tertiary)]">
                    No schemas available
                  </p>
                ) : (
                  schemas.map((schema) => {
                    const checked = selectedSchemaIds.includes(schema.id);
                    return (
                      <DropdownMenu.CheckboxItem
                        key={schema.id}
                        checked={checked}
                        onCheckedChange={() => onSchemaToggle(schema.id)}
                        className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2.5 text-sm outline-none data-[highlighted]:bg-[var(--color-bg-secondary)]"
                      >
                        <DropdownMenu.ItemIndicator>
                          <Check className="h-4 w-4 text-[var(--color-brand-primary)]" />
                        </DropdownMenu.ItemIndicator>
                        {/* Reserve space when unchecked so text stays aligned */}
                        {!checked && <span className="w-4" />}
                        <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                          <span className="truncate">{schema.name}</span>
                          <SchemaStatusBadge status={schema.status} />
                        </span>
                      </DropdownMenu.CheckboxItem>
                    );
                  })
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          {selectedSchemaIds.length > 0 && (
            <p className="mt-1.5 text-xs text-[var(--color-text-tertiary)]">
              {selectedSchemaIds.length} schema{selectedSchemaIds.length !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>
      )}
    </div>
  );
}
